import { TranslationProvider } from "@/contexts/TranslationContext";
import LanguageSelector from "@/components/LanguageSelector";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import DynamicFavicon from "./components/DynamicFavicon";
import PackageTours from "./pages/PackageTours";
import Home from "./pages/Home";
import About from "./pages/About";
import Tours from "./pages/Tours";
import TourDetail from "./pages/TourDetail";
import Checkout from "./pages/Checkout";
import Transfers from "./pages/Transfers";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSettings from "./pages/AdminSettings";
import AdminCategories from "./pages/AdminCategories";
import AdminPaymentMethods from "./pages/AdminPaymentMethods";
import AdminEmailSettings from "./pages/AdminEmailSettings";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminTours from "./pages/AdminTours";
import AdminTransfers from "./pages/AdminTransfers";
import AdminBookings from "./pages/AdminBookings";
import AdminTourBookings from "./pages/AdminTourBookings";
import AdminVehicleImages from "./pages/AdminVehicleImages";
import AdminBlogs from "./pages/AdminBlogs";
import AdminLanguages from "./pages/AdminLanguages";
import AdminTranslationDictionary from "./pages/AdminTranslationDictionary";
import AdminTranslationSettings from "./pages/AdminTranslationSettings";
import AdminTestimonials from "./pages/AdminTestimonials";
import { AdminSeoConfigurations } from "./pages/AdminSeoConfigurations";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import CarRental from "./pages/CarRental";
import VehicleDetail from "./pages/VehicleDetail";
import AdminRentalVehicles from "./pages/AdminRentalVehicles";
import AdminRentalBookings from "./pages/AdminRentalBookings";
import AdminTransferDestinations from "./pages/AdminTransferDestinations";
import AdminTransferExtras from "./pages/AdminTransferExtras";
import AdminShuttleBookings from "./pages/AdminShuttleBookings";
import AdminTransferAirports from "./pages/AdminTransferAirports";
import AdminTransferHotels from "./pages/AdminTransferHotels";
import AdminShuttleVehicles from "./pages/AdminShuttleVehicles";
import AdminServiceTypes from "./pages/AdminServiceTypes";
import AdminZonesPricing from "./pages/AdminZonesPricing";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import TransferDestinations from "./pages/TransferDestinations";
import TransferDestinationPage from "./pages/TransferDestinationPage";
import RentalBookingConfirmation from "./pages/RentalBookingConfirmation";
import { BookingDetails } from "./pages/BookingDetails";
import VehicleSelection from "./pages/VehicleSelection";
import ShuttleBookingPage from "./pages/ShuttleBookingPage";
import ShuttleConfirmation from "./pages/ShuttleConfirmation";
import PaymentPage from "./pages/PaymentPage";
import BookingConfirmation from "./pages/BookingConfirmation";
import TourBookingConfirmation from "./pages/TourBookingConfirmation";
import PayPalSuccess from "./pages/PayPalSuccess";
import PayPalCancel from "./pages/PayPalCancel";
import PayPalSuccessTour from "./pages/PayPalSuccessTour";
import PayPalCancelTour from "./pages/PayPalCancelTour";
import FAQ from "./pages/FAQ";
import RealEstate from "./pages/RealEstate";
import PropertyDetail from "./pages/PropertyDetail";
import AdminProperties from "./pages/AdminProperties";
import AdminPropertyInquiries from "./pages/AdminPropertyInquiries";
import AdminHeroSlides from "./pages/AdminHeroSlides";
import AdminPageContent from "./pages/AdminPageContent";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <TranslationProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <DynamicFavicon />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/tours" element={<Tours />} />
          <Route path="/package-tours" element={<PackageTours />} />
          <Route path="/tour/:slug" element={<TourDetail />} />
           <Route path="/blog" element={<Blog />} />
           <Route path="/blog/:slug" element={<BlogDetail />} />
           <Route path="/car-rental" element={<CarRental />} />
           <Route path="/car-rental/:slug" element={<VehicleDetail />} />
           <Route path="/checkout" element={<Checkout />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/transfer-destinations" element={<TransferDestinations />} />
          <Route path="/transfer-destinations/:slug" element={<TransferDestinationPage />} />
          {/* Redirect old Spanish URLs */}
          <Route path="/traslados" element={<TransferDestinations />} />
          <Route path="/traslados/:slug" element={<TransferDestinationPage />} />
           <Route path="/booking" element={<BookingDetails />} />
           <Route path="/shuttle/vehicles" element={<VehicleSelection />} />
           <Route path="/shuttle/booking" element={<ShuttleBookingPage />} />
           <Route path="/shuttle/confirmation" element={<ShuttleConfirmation />} />
           <Route path="/shuttle/payment" element={<PaymentPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/real-estate" element={<RealEstate />} />
          <Route path="/real-estate/:slug" element={<PropertyDetail />} />
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/tours" element={<AdminTours />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/transfers" element={<AdminTransfers />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/tour-bookings" element={<AdminTourBookings />} />
            <Route path="/admin/vehicle-images" element={<AdminVehicleImages />} />
            <Route path="/admin/blogs" element={<AdminBlogs />} />
            <Route path="/admin/languages" element={<AdminLanguages />} />
            <Route path="/admin/translation-dictionary" element={<AdminTranslationDictionary />} />
            <Route path="/admin/translation-settings" element={<AdminTranslationSettings />} />
            <Route path="/admin/testimonials" element={<AdminTestimonials />} />
            <Route path="/admin/properties" element={<AdminProperties />} />
            <Route path="/admin/property-inquiries" element={<AdminPropertyInquiries />} />
            <Route path="/admin/rental-vehicles" element={<AdminRentalVehicles />} />
            <Route path="/admin/rental-bookings" element={<AdminRentalBookings />} />
            <Route path="/admin/transfer-destinations" element={<AdminTransferDestinations />} />
            <Route path="/admin/transfer-extras" element={<AdminTransferExtras />} />
            <Route path="/admin/shuttle-bookings" element={<AdminShuttleBookings />} />
            <Route path="/admin/transfer-airports" element={<AdminTransferAirports />} />
            <Route path="/admin/transfer-hotels" element={<AdminTransferHotels />} />
            <Route path="/admin/shuttle-vehicles" element={<AdminShuttleVehicles />} />
            <Route path="/admin/service-types" element={<AdminServiceTypes />} />
            <Route path="/admin/zones-pricing" element={<AdminZonesPricing />} />
            <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
          <Route path="/admin/payment-methods" element={<AdminPaymentMethods />} />
          <Route path="/admin/email-settings" element={<AdminEmailSettings />} />
          <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/seo-configurations" element={<AdminSeoConfigurations />} />
          <Route path="/admin/hero-slides" element={<AdminHeroSlides />} />
          <Route path="/admin/page-content" element={<AdminPageContent />} />
          <Route path="/booking-confirmation" element={<BookingConfirmation />} />
          <Route path="/tour-booking-confirmation" element={<TourBookingConfirmation />} />
          <Route path="/paypal-success" element={<PayPalSuccess />} />
          <Route path="/paypal-cancel" element={<PayPalCancel />} />
           <Route path="/paypal-success-tour" element={<PayPalSuccessTour />} />
           <Route path="/paypal-cancel-tour" element={<PayPalCancelTour />} />
           <Route path="/rental-booking-confirmation" element={<RentalBookingConfirmation />} />
           {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
           <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TranslationProvider>
  </TooltipProvider>
</QueryClientProvider>
);

export default App;
